import { useState } from "react";
import { makeStyles } from "tss-react/mui";
import { MiTHEME } from "@miplatform/theme";
import { Box, Popper } from "@mui/material";

const arrowStyle = {
  "&::before": {
    content: '""',
    margin: "auto",
    display: "block",
    borderStyle: "solid",
  },
};

/**
 * MiPopper is a wrapper component around MUI's Popper that provides
 * additional features such as an optional arrow and custom styling.
 *
 * @component
 * @param {object} props - Component props
 * @param {HTMLElement|SVGElement|null} [props.anchorEl=null] - The element to which the popper is anchored.
 * @param {React.ReactNode} [props.children=null] - The content to display inside the popper.
 * @param {boolean} [props.showArrow=false] - Whether to display an arrow pointing to the anchor element.
 * @param {'auto'|'auto-start'|'auto-end'|'top'|'bottom'|'right'|'left'|'top-start'|'top-end'|'bottom-start'|'bottom-end'|'right-start'|'right-end'|'left-start'|'left-end'} [props.placement='auto-end'] - The placement of the popper relative to the anchor element.
 * @param {string} [props.arrowColor=MiTHEME.palette.bluegrey.dark] - The color of the arrow.
 * @param {boolean} [props.open] - Controls the open state of the popper. If not provided, it will be determined by the presence of anchorEl.
 * @param {object} [props.sx] - Custom styles to apply to the popper.
 *
 * @returns {JSX.Element} The rendered Popper component with optional arrow.
 */
export function MiPopper({
  anchorEl = null,
  children = null,
  showArrow = false,
  placement = "auto-end",
  arrowColor = MiTHEME.palette.bluegrey.dark,
  open,
  sx = {},
}) {
  const [arrowRef, setArrowRef] = useState(null);

  const { classes } = useStyles({ arrowColor });

  const isOpen = open ?? Boolean(anchorEl);
  const id = isOpen ? "simple-popper" : undefined;

  return (
    <Popper
      id={id}
      open={isOpen}
      placement={placement}
      anchorEl={anchorEl}
      className={classes.popper}
      sx={sx}
      modifiers={[
        {
          name: "arrow",
          enabled: showArrow,
          options: {
            element: arrowRef,
          },
        },
      ]}
      data-testid="popper"
    >
      {showArrow && <Box className="arrow" ref={setArrowRef} sx={arrowStyle} />}
      {children}
    </Popper>
  );
}

const useStyles = makeStyles()((theme, { arrowColor }) => ({
  popper: {
    zIndex: theme.zIndex.popper,
    '&[data-popper-placement*="bottom"] .arrow': {
      top: 0,
      left: 0,
      marginTop: -7,
      width: 2,
      height: 5,
      borderColor: arrowColor,
      "&::before": {
        borderWidth: "0 7px 7px 7px",
        borderColor: `transparent transparent ${arrowColor} transparent`,
      },
    },
    '&[data-popper-placement*="top"] .arrow': {
      bottom: 0,
      left: 0,
      marginBottom: -7,
      width: 2,
      height: 5,
      borderColor: arrowColor,
      "&::before": {
        borderWidth: "7px 7px 0 7px",
        borderColor: `${arrowColor} transparent transparent transparent`,
      },
    },
    '&[data-popper-placement*="right"] .arrow': {
      left: 0,
      marginLeft: -7,
      width: 5,
      borderColor: arrowColor,
      "&::before": {
        borderWidth: "7px 7px 7px 0",
        borderColor: `transparent ${arrowColor} transparent transparent`,
      },
    },
    '&[data-popper-placement*="left"] .arrow': {
      right: 0,
      marginRight: -5,
      width: 5,
      borderColor: arrowColor,
      "&::before": {
        borderWidth: "7px 0 7px 7px",
        borderColor: `transparent transparent transparent ${arrowColor}`,
      },
    },
  },
}));
